import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Mail, Phone, Linkedin, MapPin, Briefcase, GraduationCap, Award, Globe2, Heart } from 'lucide-react';

const Lebenslauf = () => {
  const navigate = useNavigate();

  // Prüfen ob der Benutzer authentifiziert ist
  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem('portfolioAuth');
    if (!isAuthenticated) {
      // Zurück zur Startseite, wenn nicht authentifiziert
      navigate('/');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header with back button */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <Button 
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-primary-dark hover:text-accent-green"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Zurück zur Startseite
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto max-w-5xl px-6 pt-24 pb-20">
        {/* Header Section */}
        <Card className="mb-8 border-2 border-gray-200 shadow-2xl overflow-hidden">
          <div className="bg-white p-8">
            <div className="flex items-center justify-between gap-6">
              {/* Photo - LEFT */}
              <div className="flex-shrink-0">
                <div className="w-44 h-44 rounded-full overflow-hidden border-4 border-primary-dark shadow-2xl">
                  <img 
                    src="/sofia-portrait.png"
                    alt="Sofia Laitenberger"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Contact Information - RIGHT */}
              <div className="flex-1">
                <h1 className="text-4xl font-bold mb-6 text-primary-dark">Sofia Laitenberger</h1>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 flex-shrink-0 mt-1 text-accent-green" />
                    <div>
                      <p className="font-semibold text-primary-dark">Adresse</p>
                      <p className="text-gray-700">Richard-Linde-Weg 1</p>
                      <p className="text-gray-700">21033 Hamburg</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 flex-shrink-0 text-accent-green" />
                    <div>
                      <p className="font-semibold text-primary-dark">Telefon</p>
                      <a href="tel:015233527031" className="text-gray-700 hover:text-accent-green transition-colors">
                        0152 33527031
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 flex-shrink-0 text-accent-green" />
                    <div>
                      <p className="font-semibold text-primary-dark">E-Mail</p>
                      <a href="mailto:sofia.laitenberger@aol.com" className="text-gray-700 hover:text-accent-green transition-colors">
                        sofia.laitenberger@aol.com
                      </a>
                    </div>
                  </div>

                  <div className="pt-2">
                    <a 
                      href="https://www.linkedin.com/in/sofia-laitenberger-2a6576155/?locale=de_DE" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Button 
                        className="bg-accent-green text-white hover:bg-primary-dark transition-all duration-300"
                      >
                        <Linkedin className="w-5 h-5 mr-2" />
                        LinkedIn Profil
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Title Bar */}
          <div className="bg-primary-dark px-8 py-4">
            <p className="text-lg font-semibold text-white text-center">
              Juristin | KI & Digitalisierung | Compliance & Prozessoptimierung | Omnichannel Marketing Konzepte
            </p>
          </div>
        </Card>

        {/* Profil */}
        <Card className="mb-8 border-2 border-gray-200 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="text-2xl text-primary-dark flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-accent-green" />
              Profil
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-gray-700 leading-relaxed">
              Juristin mit langjähriger Erfahrung im öffentlichen Sektor sowie an der Schnittstelle von Recht, 
              Digitalisierung und Künstlicher Intelligenz. Erfahren in der Analyse komplexer Verwaltungs- und 
              Geschäftsprozesse, der Identifikation von Digitalisierungspotenzialen sowie der Entwicklung 
              KI-gestützter Lösungsansätze. Kombination aus juristischer Fachkompetenz, Projektmanagement-Erfahrung, 
              Prozessanalyse und technologischem Verständnis mit Schwerpunkt auf KI-Transformation, AI Governance, 
              Compliance und organisatorischem Change-Management. Routiniert in der Kommunikation zwischen 
              Fachbereichen, Verwaltung, Management und technischen Teams.
            </p>
          </CardContent>
        </Card>

        {/* Berufserfahrung */}
        <Card className="mb-8 border-2 border-gray-200 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="text-2xl text-primary-dark flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-accent-green" />
              Berufserfahrung
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Position 1 */}
            <div className="border-l-4 border-accent-green pl-6">
              <h3 className="text-xl font-bold text-primary-dark mb-1">
                Spezialisierung KI-Datenanalyse, Legal Tech & Compliance
              </h3>
              <p className="text-accent-green font-semibold mb-3">12/2025 – 06/2026</p>
              <ul className="list-disc pl-5 space-y-2 text-gray-700">
                <li>Analyse von Verwaltungs- und Geschäftsprozessen hinsichtlich KI-gestützter Automatisierungspotenziale</li>
                <li>Entwicklung von Konzepten für den Einsatz von Generativer KI und KI-Agenten in Wissensmanagement-, Dokumentations- und Verwaltungsprozessen</li>
                <li>Bewertung von KI-Anwendungsfällen unter Berücksichtigung von Datenschutz, Compliance und AI Governance</li>
                <li>Konzeption von Pseudonymisierungs- und Datenschutzkonzepten für KI-Anwendungen</li>
                <li>Anwendung moderner KI-Systeme zur Prozessanalyse, Wissensaufbereitung und Entscheidungsunterstützung</li>
              </ul>
            </div>

            {/* Position 2 */}
            <div className="border-l-4 border-primary-light pl-6">
              <h3 className="text-xl font-bold text-primary-dark mb-1">
                Juristische Sachbearbeiterin Leistungsrecht
              </h3>
              <p className="text-sm text-gray-600 mb-1">Amt für Migration, Hamburg</p>
              <p className="text-accent-green font-semibold mb-3">10/2024 – 06/2025</p>
              <ul className="list-disc pl-5 space-y-2 text-gray-700">
                <li>Analyse komplexer Verwaltungsverfahren und rechtlicher Entscheidungsprozesse</li>
                <li>Identifikation und Strukturierung informationsintensiver Arbeitsabläufe</li>
                <li>Steuerung digitaler Akten- und Dokumentationsprozesse</li>
                <li>Entwicklung strukturierter Lösungsansätze für komplexe Einzelfälle</li>
              </ul>
            </div>

            {/* Position 3 */}
            <div className="border-l-4 border-primary-light pl-6">
              <h3 className="text-xl font-bold text-primary-dark mb-1">
                Juristische Sachbearbeiterin – Widerspruchsverfahren
              </h3>
              <p className="text-sm text-gray-600 mb-1">Altersversorgungswerk der Zahnärztekammer Niedersachsen (über Hays)</p>
              <p className="text-accent-green font-semibold mb-3">11/2023 – 04/2024</p>
              <ul className="list-disc pl-5 space-y-2 text-gray-700">
                <li>Juristische Prüfung und Bearbeitung umfangreicher Widerspruchsverfahren</li>
                <li>Analyse komplexer Einzelfälle und strukturierte Sachverhaltsbewertung</li>
                <li>Optimierung standardisierter Bearbeitungsprozesse</li>
              </ul>
            </div>

            {/* Position 4 */}
            <div className="border-l-4 border-primary-light pl-6">
              <h3 className="text-xl font-bold text-primary-dark mb-1">
                Juristische Sachbearbeiterin – Leistungsrecht & Compliance
              </h3>
              <p className="text-sm text-gray-600 mb-1">Bundesagentur für Arbeit, Hamburg</p>
              <p className="text-accent-green font-semibold mb-3">04/2023 – 11/2023</p>
              <ul className="list-disc pl-5 space-y-2 text-gray-700">
                <li>Analyse großer Fallbestände und komplexer Leistungsprozesse</li>
                <li>Bewertung regelbasierter Entscheidungsverfahren</li>
                <li>Identifikation von Optimierungspotenzialen in Verwaltungsabläufen</li>
              </ul>
            </div>

            {/* Position 5 */}
            <div className="border-l-4 border-primary-light pl-6">
              <h3 className="text-xl font-bold text-primary-dark mb-1">
                Project Managerin & Consultant
              </h3>
              <p className="text-sm text-gray-600 mb-1">IXL Germany / London</p>
              <p className="text-accent-green font-semibold mb-3">09/1998 – 05/2004</p>
              <ul className="list-disc pl-5 space-y-2 text-gray-700">
                <li>Leitung internationaler Digital- und Transformationsprojekte</li>
                <li>Beratung von Kunden hinsichtlich Prozess- und Organisationsoptimierung</li>
                <li>Stakeholder Management auf Management-Ebene</li>
                <li>Kunden u. a.: Sony, Unilever, BlueYonder</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Ausbildung */}
        <Card className="mb-8 border-2 border-gray-200 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="text-2xl text-primary-dark flex items-center gap-2">
              <GraduationCap className="w-6 h-6 text-accent-green" />
              Ausbildung
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div>
              <h3 className="text-lg font-bold text-primary-dark">Rechtsreferendariat</h3>
              <p className="text-gray-600">Oberlandesgericht Celle | 09/2015 – 02/2018</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-primary-dark">Auslandsstation</h3>
              <p className="text-gray-600">Deutsches Generalkonsulat Rio de Janeiro | 05/2017 – 08/2017</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-primary-dark">Studium der Rechtswissenschaften</h3>
              <p className="text-gray-600">Universität Hamburg | Erste Juristische Prüfung | 04/2006 – 05/2015</p>
            </div>
          </CardContent>
        </Card>

        {/* Kernkompetenzen */}
        <Card className="mb-8 border-2 border-gray-200 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="text-2xl text-primary-dark flex items-center gap-2">
              <Award className="w-6 h-6 text-accent-green" />
              Kernkompetenzen
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-bold text-primary-dark mb-3">Legal & Compliance</h3>
                <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm">
                  <li>Wirtschaftsrecht</li>
                  <li>Widerspruchs- und Schadensersatzrecht</li>
                  <li>Compliance (DSGVO / EU-AI Act)</li>
                  <li>AI Governance</li>
                  <li>Verfahrenskoordination</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-primary-dark mb-3">Tools & Systeme</h3>
                <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm">
                  <li>KI-Tools (ChatGPT, Claude, Perplexity)</li>
                  <li>AI Agents (n8n, Langdock)</li>
                  <li>Microsoft Office, SAP, eAkte</li>
                  <li>Workflows (Bitrix, Brevo)</li>
                  <li>Design Tools (Canva, Gamma, Miro)</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sprachen */}
        <Card className="mb-8 border-2 border-gray-200 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="text-2xl text-primary-dark flex items-center gap-2">
              <Globe2 className="w-6 h-6 text-accent-green" />
              Sprachen
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="text-center p-3 bg-accent-green/10 rounded-lg">
                <p className="font-bold text-primary-dark">Deutsch</p>
                <p className="text-sm text-gray-600">Muttersprache</p>
              </div>
              <div className="text-center p-3 bg-accent-green/10 rounded-lg">
                <p className="font-bold text-primary-dark">Portugiesisch</p>
                <p className="text-sm text-gray-600">Muttersprache</p>
              </div>
              <div className="text-center p-3 bg-accent-green/10 rounded-lg">
                <p className="font-bold text-primary-dark">Englisch</p>
                <p className="text-sm text-gray-600">Sehr gut</p>
              </div>
              <div className="text-center p-3 bg-accent-green/10 rounded-lg">
                <p className="font-bold text-primary-dark">Französisch</p>
                <p className="text-sm text-gray-600">Sehr gut</p>
              </div>
              <div className="text-center p-3 bg-accent-green/10 rounded-lg">
                <p className="font-bold text-primary-dark">Spanisch</p>
                <p className="text-sm text-gray-600">Sehr gut</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Interessen */}
        <Card className="border-2 border-gray-200 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="text-2xl text-primary-dark flex items-center gap-2">
              <Heart className="w-6 h-6 text-accent-green" />
              Interessen
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-gray-700">Digitale Transformation · Reisen · Kunst · Kulturen · Sport</p>
          </CardContent>
        </Card>

        {/* Footer with date */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Stand: 12.06.2026</p>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <Button 
            onClick={() => navigate('/#contact')}
            size="lg"
            className="bg-accent-green hover:bg-accent-light text-white font-semibold px-8"
          >
            Kontakt aufnehmen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Lebenslauf;
